import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { bgVariants, colors } from '../theme/tokens';

// Picks a background gradient variant from the current path so the backdrop
// "slightly shifts" as the user moves between sections.
function variantForPath(pathname) {
  if (pathname.startsWith('/login')) return bgVariants.login;
  if (pathname === '/' || pathname.startsWith('/landing')) return bgVariants.landing;
  if (pathname.startsWith('/car-parts/find')) return bgVariants.find;
  if (pathname.startsWith('/car-parts/add')) return bgVariants.add;
  if (pathname.startsWith('/car-parts')) return bgVariants.home;
  if (pathname.startsWith('/client-tracking')) return bgVariants.clients;
  if (pathname.startsWith('/support-inbox')) return bgVariants.inbox;
  if (pathname.startsWith('/profile-match')) return bgVariants.profile;
  if (pathname.startsWith('/nfc-card')) return bgVariants.nfc;
  if (pathname.startsWith('/task-tracking')) return bgVariants.tasks;
  if (pathname.startsWith('/admin')) return bgVariants.admin;
  return bgVariants.default;
}

// Fixed full-viewport backdrop that cross-fades between per-route gradients
// (CSS can't tween gradients, so we stack two layers and fade opacity), plus a
// fade-in on routed content for each navigation.
const PageTransition = ({ children }) => {
  const location = useLocation();
  const initial = variantForPath(location.pathname);

  // layerA / layerB hold gradients; `top` says which one is currently visible.
  const [layers, setLayers] = useState({ a: initial, b: initial });
  const [topLayer, setTopLayer] = useState('a');
  const current = useRef(initial);

  useEffect(() => {
    const next = variantForPath(location.pathname);
    if (next === current.current) return;
    current.current = next;

    // Paint the next gradient onto the hidden layer, then flip which is on top.
    setLayers((prev) =>
      topLayer === 'a' ? { ...prev, b: next } : { ...prev, a: next }
    );
    const id = requestAnimationFrame(() => setTopLayer((t) => (t === 'a' ? 'b' : 'a')));
    return () => cancelAnimationFrame(id);
  }, [location.pathname, topLayer]);

  const layerStyle = (visible, image) => ({
    position: 'fixed',
    inset: 0,
    zIndex: -1,
    backgroundColor: colors.bg,
    backgroundImage: image,
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.9s ease'
  });

  return (
    <>
      <div aria-hidden="true" style={layerStyle(topLayer === 'a', layers.a)} />
      <div aria-hidden="true" style={layerStyle(topLayer === 'b', layers.b)} />
      <div key={'/' + (location.pathname.split('/')[1] || '')} className="page-fade" style={{ minHeight: '100vh' }}>
        {children}
      </div>
    </>
  );
};

export default PageTransition;
